import { inject, ref } from 'vue'
import { getCurrentAge } from '@/utils/dateTime.js'
import { useCustomerPrescriptions } from '@/composables/useCustomerPrescriptions.js'
import { ORDER_LIST_FIELDS, ORDER_LIST_INCLUDES } from '@/constants/order.js'
import { insuranceIsActive } from '@/utils/insurance.js'

// TECH-822 — deterministic CommerceLayer reads behind the customer summary, replacing the
// smart-customer-profile Cloud Function that used to generate it.

const RECENT_ORDERS_PAGE_SIZE = 25

// The server sorts prescriptions on created_at but the card ranks them by exam date, so pull
// a window wide enough that a late-dated older record still reaches the client-side sort.
const PRESCRIPTION_PAGE_SIZE = 25

// After-sales cases are grouped by return reference, using the values Returns.vue and
// DayOverviewRefunds.vue already filter on. Only settled cases count here: `refunded` means
// money went back, `completed` that the case was closed, and
// useAfterSales.markReturnAsCompleted() writes them as a single-element tag array, so a return
// carries at most one. Anything still in flight is not yet a fact about the customer.
const AFTER_SALES_REFERENCES = ['return', 'intolerance']
const AFTER_SALES_SETTLED_TAGS = 'completed,refunded'

// Insurance cases are counted on their own terms rather than as a third settled-case row: the
// question is how many claims the customer raised, so open ones count too.
const INSURANCE_CLAIM_REFERENCE = 'insurance'
const INSURANCE_CLAIM_STATUSES = 'draft,requested,approved,shipped,received'

// Insurance is a single SKU whose tier lives in metadata.title ('VIUplus A' / 'B' / 'C'), set
// by ProductView when the extra is added — there is no per-tier sku_code to group on. Counts
// the policies in force, which is a different question from the VIUplus cases counted above:
// the order must be approved, and the policy must still be active. Expiry is a date comparison
// CL cannot filter on, so insuranceIsActive() applies it client-side.
// Same line-item filter shape as CustomerPrescriptions' adjusted-prescription query.
const INSURANCE_LINE_ITEM_FILTER = '{ "type": "insurance"}'

// The active check cannot be pushed into the filter, so the rows have to be walked rather than
// counted server-side. CL caps a page at 25, so clUtils.retrieveAll pages through — capped at
// 100, well past any real customer, so a runaway query cannot walk an org's whole line items.
const INSURANCE_LIMIT = 100

// The last-order card renders CustomerOrderItem, so it reads with the shared order-list query
// shape. Kept as its own small query rather than folded into fetchRecentOrders — loading 25
// orders at this depth to display one would be the expensive way round.
const LAST_ORDER_PAGE_SIZE = 5

// Frequent-returns badge — thresholds provisional, see docs/customer-summary.md §3a.
// COUNTABLE_RETURN_REFERENCE is confirmed: Returns.vue and DayOverviewRefunds.vue both filter
// on reference_eq: 'return'.
const ENABLE_FREQUENT_RETURNS_BADGE = true
const COUNTABLE_RETURN_REFERENCE = 'return'
const COUNTABLE_RETURN_TAG = 'refunded'
const RETURN_RATE_THRESHOLD = 0.1
const MIN_RETURN_ORDERS = 2
const MIN_ORDERS = 3
const WINDOW_MONTHS = 24

// Cancelled before approval — never a real order. CustomerOrders.getOrders() subtracts these
// from whichever page it happens to hold, which drifts as you paginate.
function isVoided(order) {
  return order.status === 'cancelled' && !order.approved_at
}

function isCountableReturn(customerReturn) {
  return (
    customerReturn.reference === COUNTABLE_RETURN_REFERENCE &&
    customerReturn.tags?.some((tag) => tag.name === COUNTABLE_RETURN_TAG)
  )
}

export function useCustomerSummary() {
  const cl = inject('cl')
  const clUtils = inject('clUtils')
  const { fetchLatestCustomerPrescription } = useCustomerPrescriptions()

  const info = ref(null)
  const prescription = ref(null)
  const lastOrder = ref(null)
  const afterSales = ref([])
  const insurances = ref([])
  const insuranceClaims = ref(0)
  const isLoading = ref(false)
  const hasError = ref(false)

  function orderFilters(customerId) {
    return {
      customer_id_eq: `${customerId}`,
      placed_at_not_null: true,
      status_matches_any: 'placed,approved,cancelled'
    }
  }

  function fetchCustomer(customerId) {
    return cl.customers.retrieve(customerId, {
      fields: { customers: ['status', 'metadata'] }
    })
  }

  function fetchRecentOrders(customerId) {
    return cl.orders.list({
      include: ['returns.tags'],
      filters: orderFilters(customerId),
      sort: { placed_at: 'desc' },
      fields: {
        orders: ['status', 'number', 'approved_at', 'placed_at', 'returns'],
        returns: ['reference', 'status', 'tags'],
        tags: ['name']
      },
      pageSize: RECENT_ORDERS_PAGE_SIZE
    })
  }

  // Voided orders stay in the filter because CL cannot express "cancelled but approved" in one
  // pass, so a small page is fetched and the first real order picked off it — the same thing
  // CustomerOrders does client-side when it builds the open-orders list.
  function fetchLastOrder(customerId) {
    return cl.orders.list({
      include: ORDER_LIST_INCLUDES,
      filters: orderFilters(customerId),
      sort: { placed_at: 'desc' },
      fields: ORDER_LIST_FIELDS,
      pageSize: LAST_ORDER_PAGE_SIZE
    })
  }

  // Counted rather than paged: only meta.recordCount is read, so the figures stay exact for a
  // customer with more after-sales cases than a page holds.
  function fetchReturnCount(customerId, filters) {
    return cl.returns.list({
      filters: { customer_id_eq: `${customerId}`, ...filters },
      fields: { returns: ['id'] },
      pageSize: 1
    })
  }

  function fetchAfterSales(customerId) {
    return Promise.all(
      AFTER_SALES_REFERENCES.map((reference) =>
        fetchReturnCount(customerId, {
          reference_eq: reference,
          tags_name_in: AFTER_SALES_SETTLED_TAGS
        })
      )
    )
  }

  // Every claim the customer actually raised, settled or still open — a different question
  // from the settled-cases count above, which is why it is not filtered on the settled tags.
  // Cancelled and rejected returns are excluded by listing the live statuses, the same set
  // DayOverviewRefunds queries on.
  function fetchInsuranceClaimCount(customerId) {
    return fetchReturnCount(customerId, {
      reference_eq: INSURANCE_CLAIM_REFERENCE,
      status_matches_any: INSURANCE_CLAIM_STATUSES
    })
  }

  function fetchInsuranceLineItems(customerId) {
    return clUtils.retrieveAll('line_items', {
      filters: {
        metadata_jcont: INSURANCE_LINE_ITEM_FILTER,
        order_customer_id_eq: `${customerId}`,
        order_status_eq: 'approved'
      },
      fields: { line_items: ['metadata', 'quantity'] },
      limit: INSURANCE_LIMIT
    })
  }

  function fetchOldestOrder(customerId) {
    return cl.orders.list({
      filters: orderFilters(customerId),
      sort: { placed_at: 'asc' },
      fields: { orders: ['placed_at'] },
      pageSize: 1
    })
  }

  // Only meta.recordCount is used — counting voided orders server-side keeps the lifetime
  // total exact instead of subtracting whatever happens to sit on page one.
  function fetchVoidedOrderCount(customerId) {
    return cl.orders.list({
      filters: {
        customer_id_eq: `${customerId}`,
        placed_at_not_null: true,
        status_eq: 'cancelled',
        approved_at_null: true
      },
      fields: { orders: ['id'] },
      pageSize: 1
    })
  }

  // Numerator counts orders with at least one countable return, not returns, so a double
  // return on one order cannot push the rate over 100%. Both sides of the ratio are scoped
  // to the same window and to the orders actually fetched.
  function buildReturnStats(recentOrders) {
    const windowStart = new Date()
    windowStart.setMonth(windowStart.getMonth() - WINDOW_MONTHS)

    const windowOrders = recentOrders.filter(
      (order) => !isVoided(order) && new Date(order.placed_at) >= windowStart
    )
    const returnOrderCount = windowOrders.filter((order) =>
      order.returns?.some(isCountableReturn)
    ).length

    return {
      returnOrderCount,
      windowOrderCount: windowOrders.length,
      frequentReturns:
        ENABLE_FREQUENT_RETURNS_BADGE &&
        returnOrderCount >= MIN_RETURN_ORDERS &&
        windowOrders.length >= MIN_ORDERS &&
        returnOrderCount / windowOrders.length >= RETURN_RATE_THRESHOLD
    }
  }

  function buildInfo(customer, recentOrders, oldestOrders, voidedOrders) {
    const placedOrders = recentOrders.meta?.recordCount ?? 0
    const voidedOrderCount = voidedOrders.meta?.recordCount ?? 0
    const birthDate = customer?.metadata?.birth_date
    const gender = customer?.metadata?.gender
    const name = [customer?.metadata?.first_name, customer?.metadata?.last_name]
      .filter(Boolean)
      .join(' ')

    return {
      name: name || null,
      age: birthDate ? getCurrentAge(birthDate) : null,
      gender: gender && gender !== 'none' ? gender : null,
      totalOrders: Math.max(placedOrders - voidedOrderCount, 0),
      oldestOrderDate: oldestOrders[0]?.placed_at ?? null,
      status: customer?.status ?? null,
      ...buildReturnStats(recentOrders)
    }
  }

  function buildEye(eye) {
    return {
      spherical: eye?.spherical ?? null,
      cylindrical: eye?.cylindrical ?? null,
      axis: eye?.axis ?? null
    }
  }

  function buildAfterSales(countResponses) {
    return AFTER_SALES_REFERENCES.map((reference, index) => ({
      reference,
      count: countResponses[index]?.meta?.recordCount ?? 0
    })).filter((group) => group.count > 0)
  }

  // Grouped on the German title because ProductView writes the same tier string into every
  // locale, so it is a stable key; the whole title object is passed on regardless, leaving the
  // language choice to the component.
  function buildInsurances(lineItems) {
    const groups = new Map()

    for (const lineItem of lineItems) {
      const titles = lineItem.metadata?.title
      const key = titles?.de ?? titles?.en

      if (!key || !insuranceIsActive(lineItem)) {
        continue
      }

      const group = groups.get(key) ?? { titles, count: 0 }
      group.count += lineItem.quantity ?? 1
      groups.set(key, group)
    }

    return [...groups.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([, group]) => group)
  }

  function buildPrescription(latest) {
    if (!latest) {
      return null
    }

    const { date, viuEyesightTest, right, left } = latest.metadata.prescription

    return {
      date: date ?? null,
      // Same test as FormPrescriptionOutput — anything not explicitly a VIU eyesight test is
      // external, so a missing flag on an older record reads as external rather than as ours.
      external: viuEyesightTest !== true,
      right: buildEye(right),
      left: buildEye(left)
    }
  }

  async function loadCustomerSummary(customerId) {
    if (!customerId) {
      return
    }

    isLoading.value = true
    hasError.value = false

    try {
      const [
        customer,
        recentOrders,
        oldestOrders,
        voidedOrders,
        latestPrescription,
        lastOrders,
        afterSalesCounts,
        insuranceLineItems,
        insuranceClaimCount
      ] = await Promise.all([
        fetchCustomer(customerId),
        fetchRecentOrders(customerId),
        fetchOldestOrder(customerId),
        fetchVoidedOrderCount(customerId),
        fetchLatestCustomerPrescription(customerId, { pageSize: PRESCRIPTION_PAGE_SIZE }),
        fetchLastOrder(customerId),
        fetchAfterSales(customerId),
        fetchInsuranceLineItems(customerId),
        fetchInsuranceClaimCount(customerId)
      ])

      info.value = buildInfo(customer, recentOrders, oldestOrders, voidedOrders)
      prescription.value = buildPrescription(latestPrescription)
      lastOrder.value = lastOrders.find((order) => !isVoided(order)) ?? null
      afterSales.value = buildAfterSales(afterSalesCounts)
      insurances.value = buildInsurances(insuranceLineItems)
      insuranceClaims.value = insuranceClaimCount.meta?.recordCount ?? 0
    } catch (error) {
      hasError.value = true
      info.value = null
      prescription.value = null
      lastOrder.value = null
      afterSales.value = []
      insurances.value = []
      insuranceClaims.value = 0
      console.error('Error loading customer summary:', error)
    } finally {
      isLoading.value = false
    }
  }

  return {
    info,
    prescription,
    lastOrder,
    afterSales,
    insurances,
    insuranceClaims,
    isLoading,
    hasError,
    loadCustomerSummary
  }
}
